import { Spacing } from '@persona-id/design-tokens';
import { ReactNode } from 'react';
import styled from 'styled-components';

import {
  MediumHeaderContainer,
  TableNullStateContainer,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/shared';
import { MATCH_REQUIREMENT_CHECK_NAMES } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/utils';
import { MatchRequirementsCheckStub } from 'dashboard/models/simulate/stubs';
import {
  MatchGroupsCheckConfig,
  MatchRequirementsCheckConfig,
} from 'dashboard/models/verification-check';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';

const MatchRequirementsStickerContainer = styled.div`
  gap: ${Spacing[1]};
  display: flex;
  flex-wrap: wrap;
`;

const getMatchRequirementsMap = (matchRequirementsCheckConfig: MatchRequirementsCheckConfig) => {
  return Object.fromEntries(
    matchRequirementsCheckConfig['check-requirements'].map((matchRequirement) => [
      matchRequirement.name,
      matchRequirement.description,
    ]),
  );
};

const getMatchGroupsMap = (matchGroupsCheckConfig: MatchGroupsCheckConfig) => {
  return Object.fromEntries(
    matchGroupsCheckConfig['match-groups'].flatMap((matchGroup) => {
      return matchGroup['match-requirements'].map((matchRequirement) => [
        matchRequirement.name,
        matchRequirement.description,
      ]);
    }),
  );
};

interface VerificationMatchRequirementChecksTableProps {
  verificationTemplateVersion: VerificationTemplateVersion;
  matchRequirementCheckStubs: MatchRequirementsCheckStub[];
  rightElement?: ReactNode;
}

export const VerificationMatchRequirementChecksTable = ({
  verificationTemplateVersion,
  matchRequirementCheckStubs,
  rightElement,
}: VerificationMatchRequirementChecksTableProps) => {
  const matchRequirementCheckConfigs = verificationTemplateVersion.attributes[
    'verification-checks'
  ].filter((check) => MATCH_REQUIREMENT_CHECK_NAMES.includes(check.check));

  return (
    <>
      <MediumHeaderContainer>
        Verification Checks with Match Requirements
        {rightElement}
      </MediumHeaderContainer>
      <Table>
        <TableHead>
          <TableHeaderCell>Check name</TableHeaderCell>
          <TableHeaderCell>Failed match requirements</TableHeaderCell>
        </TableHead>
        {matchRequirementCheckStubs.length > 0 && (
          <TableBody>
            {matchRequirementCheckStubs.map((stub, idx) => {
              const checkName = stub.name;
              const checkConfig = matchRequirementCheckConfigs.find(
                (config) => config.check === checkName,
              );

              if (checkConfig == null) {
                return <></>;
              }

              const isLegacy =
                'check-requirements' in checkConfig &&
                (checkConfig as MatchRequirementsCheckConfig)['check-requirements'].length > 0;

              const matchRequirementToReadable = isLegacy
                ? getMatchRequirementsMap(checkConfig as MatchRequirementsCheckConfig)
                : getMatchGroupsMap(checkConfig as MatchGroupsCheckConfig);

              const checkNameReadable = CHECK_DISPLAY_CONFIG[checkName].name;

              return (
                <TableRow key={idx}>
                  <TableCell breakWord>{checkNameReadable}</TableCell>
                  <TableCell breakWord>
                    <MatchRequirementsStickerContainer>
                      {stub['match-requirement-stubs'].map((stub, idx) => (
                        <Sticker key={idx}>
                          {matchRequirementToReadable[stub.name] || stub.name}
                        </Sticker>
                      ))}
                    </MatchRequirementsStickerContainer>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        )}
      </Table>
      {matchRequirementCheckStubs.length === 0 && (
        <TableNullStateContainer>
          <Paragraph>No overrides specified.</Paragraph>
        </TableNullStateContainer>
      )}
    </>
  );
};
